import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ArrowLeft,
  Lock,
  Database,
  UserCheck,
  FileText,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";

export default function LgpdPage() {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <img
              src="/marketing/logo_official.png"
              alt="OdontoAssist AI"
              className="h-14 w-auto object-contain"
            />
          </Link>

          <Link
            to="/"
            className="text-indigo-600 flex items-center gap-2 font-semibold hover:text-indigo-800"
          >
            <ArrowLeft size={16} />
            Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 space-y-12 [&_p]:text-justify [&_li]:text-justify">
        <section className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-50 text-indigo-600 mb-6">
            <ShieldCheck size={34} />
          </div>

          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            LGPD e Proteção de Dados
          </h1>

          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            O OdontoAssist AI foi desenvolvido com foco em privacidade, segurança
            e responsabilidade no tratamento de dados pessoais e dados sensíveis
            de saúde, em conformidade com a Lei Geral de Proteção de Dados
            Pessoais — Lei nº 13.709/2018.
          </p>
        </section>

        <section className="bg-indigo-50 border border-indigo-100 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <CheckCircle2 className="text-indigo-600" />
            Nosso compromisso
          </h2>

          <p className="text-gray-700 leading-relaxed">
            A plataforma adota medidas técnicas e administrativas para proteger
            as informações armazenadas, reduzir riscos de acesso indevido e
            permitir que clínicas e profissionais utilizem o sistema de forma
            mais segura e organizada. O compromisso do OdontoAssist AI é oferecer
            uma estrutura tecnológica adequada para apoiar o cumprimento da LGPD
            na rotina odontológica.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Papéis no tratamento dos dados
          </h2>

          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Para fins da LGPD, a clínica odontológica ou o profissional que
              utiliza o OdontoAssist AI normalmente atua como
              <strong> controlador dos dados</strong>, pois define quais dados
              serão coletados, para quais finalidades serão utilizados e por
              quanto tempo serão mantidos.
            </p>

            <p>
              O OdontoAssist AI atua como <strong>operador</strong>, fornecendo
              a infraestrutura tecnológica para armazenamento, organização e
              processamento das informações dentro da plataforma, conforme as
              instruções do controlador e os termos contratados.
            </p>
            <p>
              Quando uma pessoa participa de mais de uma clínica, cada vínculo possui contexto e permissões
              próprios. A troca de clínica não reúne nem transfere automaticamente dados entre controladores.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Dados pessoais e dados sensíveis
          </h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            Por se tratar de uma plataforma voltada à gestão odontológica, o
            sistema pode armazenar dados pessoais e dados sensíveis relacionados
            à saúde dos pacientes, tais como:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>dados cadastrais de pacientes e responsáveis;</li>
            <li>informações de contato;</li>
            <li>anamnese e histórico de saúde informado pelo paciente;</li>
            <li>prontuários, evoluções clínicas e odontogramas;</li>
            <li>documentos clínicos, contratos e termos assinados;</li>
            <li>imagens, arquivos e anexos vinculados ao atendimento.</li>
            <li>dados de agenda e, quando autorizado, informações necessárias à sincronização com calendários externos.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Bases legais aplicáveis
          </h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            O tratamento de dados na plataforma pode se apoiar, conforme o caso,
            em bases legais previstas na LGPD, incluindo:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>execução de contrato ou procedimentos preliminares;</li>
            <li>cumprimento de obrigação legal ou regulatória;</li>
            <li>exercício regular de direitos;</li>
            <li>proteção da vida ou da incolumidade física;</li>
            <li>tutela da saúde, quando aplicável;</li>
            <li>consentimento, quando necessário para determinada finalidade.</li>
          </ul>
        </section>

        <section className="grid md:grid-cols-2 gap-6">
          <div className="border border-gray-200 rounded-2xl p-6">
            <Lock className="text-indigo-600 mb-4" size={28} />
            <h3 className="text-xl font-bold mb-2">Acesso protegido</h3>
            <p className="text-gray-700 leading-relaxed">
              O acesso é individual, com controle de sessão, papéis, permissões específicas e escopos
              opcionais de pacientes e agenda. A autenticação em dois fatores pode ser ativada pelo usuário.
            </p>
          </div>

          <div className="border border-gray-200 rounded-2xl p-6">
            <Database className="text-indigo-600 mb-4" size={28} />
            <h3 className="text-xl font-bold mb-2">Dados segregados</h3>
            <p className="text-gray-700 leading-relaxed">
              Cada clínica acessa somente seus próprios dados, com organização
              lógica por conta, usuário e clínica, evitando exposição indevida
              entre ambientes distintos.
            </p>
          </div>

          <div className="border border-gray-200 rounded-2xl p-6">
            <ShieldCheck className="text-indigo-600 mb-4" size={28} />
            <h3 className="text-xl font-bold mb-2">Comunicação segura</h3>
            <p className="text-gray-700 leading-relaxed">
              A comunicação com a plataforma ocorre por conexão protegida via
              HTTPS, reduzindo riscos de interceptação dos dados em trânsito.
            </p>
          </div>

          <div className="border border-gray-200 rounded-2xl p-6">
            <FileText className="text-indigo-600 mb-4" size={28} />
            <h3 className="text-xl font-bold mb-2">Rastreabilidade</h3>
            <p className="text-gray-700 leading-relaxed">
              A plataforma mantém registros de ações relevantes e destaca exportações de prontuários para
              revisão administrativa, apoiando auditoria, responsabilização e investigação de ocorrências.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Direitos dos titulares
          </h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            Nos termos da LGPD, os titulares dos dados podem exercer direitos
            como:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>confirmação da existência de tratamento;</li>
            <li>acesso aos dados pessoais;</li>
            <li>correção de dados incompletos, inexatos ou desatualizados;</li>
            <li>anonimização, bloqueio ou eliminação, quando aplicável;</li>
            <li>portabilidade, observados os requisitos legais;</li>
            <li>informações sobre compartilhamento;</li>
            <li>revogação do consentimento, quando essa for a base utilizada.</li>
          </ul>

          <p className="text-gray-700 leading-relaxed mt-4">
            Como regra, essas solicitações devem ser encaminhadas à clínica ou
            ao profissional responsável pelo atendimento, que atua como
            controlador dos dados. O OdontoAssist AI poderá apoiar tecnicamente
            o controlador no atendimento dessas solicitações.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Retenção, exclusão e exportação
          </h2>

          <p className="text-gray-700 leading-relaxed">
            Os dados são mantidos conforme a finalidade do tratamento, as
            obrigações legais e regulatórias aplicáveis à atividade odontológica
            e as instruções do controlador. Quando solicitado e juridicamente
            cabível, os dados poderão ser corrigidos, exportados, bloqueados ou
            excluídos, respeitando deveres profissionais, fiscais, regulatórios
            e de preservação do prontuário.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Google Calendar e controle pelo usuário
          </h2>

          <p className="text-gray-700 leading-relaxed">
            Quando o profissional ativa voluntariamente a integração com o Google
            Calendar, o OdontoAssist AI envia os dados necessários para criar e manter
            os eventos de atendimento, incluindo data, horário, duração, procedimento,
            profissional e identificação reduzida do paciente. A integração não importa
            compromissos pessoais do Google para a plataforma.
          </p>

          <p className="text-gray-700 leading-relaxed mt-4">
            A autorização pode ser revogada a qualquer momento no OdontoAssist AI ou
            na Conta Google. Tokens de acesso são armazenados de forma criptografada e
            utilizados somente para a sincronização autorizada. A senha da Conta Google
            não é acessada nem armazenada pelo OdontoAssist AI.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Compartilhamento de dados
          </h2>

          <p className="text-gray-700 leading-relaxed">
            O OdontoAssist AI não comercializa dados pessoais de pacientes. O
            compartilhamento de informações ocorre apenas quando necessário para
            a prestação do serviço, cumprimento de obrigação legal, integração
            técnica autorizada, suporte operacional ou conforme instrução do
            controlador.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Segurança e boas práticas
          </h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            Entre as práticas adotadas pela plataforma estão:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>controle de acesso por autenticação;</li>
            <li>segregação de dados por clínica/usuário;</li>
            <li>uso de HTTPS para proteção dos dados em trânsito;</li>
            <li>ambientes separados para testes e produção;</li>
            <li>backups e rotinas administrativas de proteção;</li>
            <li>restrição de acesso administrativo;</li>
            <li>monitoramento e registros de ações relevantes.</li>
          </ul>
        </section>

        <section className="bg-amber-50 border border-amber-200 rounded-3xl p-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <AlertTriangle className="text-amber-600" />
            Responsabilidades da clínica
          </h2>

          <p className="text-gray-700 leading-relaxed mb-4">
            A conformidade com a LGPD depende também das práticas adotadas pela
            clínica ou profissional usuário da plataforma. É responsabilidade do
            controlador:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>coletar dados apenas quando necessários;</li>
            <li>informar o paciente sobre o uso de seus dados;</li>
            <li>obter consentimento quando aplicável;</li>
            <li>manter usuários e senhas sob controle;</li>
            <li>evitar compartilhamento indevido de acessos;</li>
            <li>revisar periodicamente permissões, sessões, colaboradores ativos e alertas de exportação;</li>
            <li>definir políticas internas de retenção e descarte;</li>
            <li>atender solicitações dos titulares quando cabíveis.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">
            Incidentes de segurança
          </h2>

          <p className="text-gray-700 leading-relaxed">
            Caso seja identificado incidente de segurança relevante que possa
            acarretar risco ou dano aos titulares, o OdontoAssist AI adotará as
            medidas técnicas e administrativas cabíveis e comunicará o
            controlador conforme a legislação aplicável, para que sejam tomadas
            as providências necessárias.
          </p>
        </section>

        <section className="border-t pt-8">
          <h2 className="text-2xl font-bold mb-4">
            Canal de contato
          </h2>

          <p className="text-gray-700 leading-relaxed">
            Dúvidas sobre privacidade, proteção de dados ou solicitações
            relacionadas à LGPD podem ser encaminhadas para
            <strong> suporte@odontoassistai.com.br</strong>, pelos demais canais oficiais
            do OdontoAssist AI ou diretamente à clínica responsável pelo tratamento
            dos dados do paciente. O OdontoAssist AI é operado pela
            <strong> Serrano Digital Solutions Ltda.</strong>
          </p>
        </section>

        <section className="text-sm text-gray-500 border-t pt-6">
          <p>
            Última atualização: 8 de agosto de 2026.
          </p>
          <p className="mt-2">
            Este conteúdo tem finalidade informativa e não substitui assessoria
            jurídica específica para cada clínica ou profissional.
          </p>
        </section>
      </main>
    </div>
  );
}
